import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "av_cookie_consent";

/**
 * Banner cookie GDPR/EDPB compliant.
 * - Nessun cookie di terze parti caricato prima del consenso.
 * - "Rifiuta" ha lo stesso peso visivo di "Accetta" (Provvedimento Garante 2021).
 * - Scelta memorizzata come JSON con timestamp per audit trail.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        // Delay per non essere invasivo al primo render
        const t = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(t);
      }
    } catch (_) {
      setVisible(true);
    }
  }, []);

  const save = (accepted) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          accepted,
          version: "1.0",
          ts: new Date().toISOString(),
        })
      );
    } catch (err) {
      console.warn("[cookie-banner] failed to save consent", err?.message || err);
    }
    setVisible(false);
    // Trigger optional custom event so third-party embeds can react
    try {
      window.dispatchEvent(
        new CustomEvent("cookie-consent-changed", { detail: { accepted } })
      );
    } catch (err) {
      console.warn("[cookie-banner] failed to dispatch consent event", err?.message || err);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[70] p-4 sm:p-6"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      data-testid="cookie-banner"
    >
      <div
        className="max-w-5xl mx-auto bg-[#141210] border border-[#E5A93C]/40 rounded-2xl shadow-2xl p-5 sm:p-6"
        style={{ boxShadow: "0 20px 60px -20px rgba(229,169,60,0.35), 0 8px 30px rgba(0,0,0,0.5)" }}
      >
        <div className="flex gap-4">
          <div className="text-[#E5A93C] pt-1 shrink-0">
            <Cookie size={28} />
          </div>
          <div className="flex-1 min-w-0">
            <h3
              id="cookie-banner-title"
              className="font-serif-display text-lg sm:text-xl text-[#F7F4EE]"
            >
              Questo sito utilizza cookie
            </h3>
            <p className="text-sm text-[#A39E93] mt-1 leading-relaxed">
              Usiamo cookie tecnici necessari al funzionamento e — solo con il
              tuo consenso — cookie di terze parti (Facebook, YouTube, Stripe)
              per gli embed social e statistiche anonime. Puoi accettare,
              rifiutare o gestire le preferenze in qualsiasi momento.{" "}
              <a
                href="/cookie"
                className="text-[#E5A93C] underline hover:text-[#F7C876]"
              >
                Cookie Policy
              </a>{" "}
              ·{" "}
              <a
                href="/privacy"
                className="text-[#E5A93C] underline hover:text-[#F7C876]"
              >
                Privacy Policy
              </a>
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => save(true)}
                className="av-pill av-pill-gold text-sm"
                data-testid="cookie-accept"
              >
                Accetta tutti
              </button>
              <button
                onClick={() => save(false)}
                className="av-pill av-pill-ghost text-sm"
                data-testid="cookie-reject"
              >
                Rifiuta cookie di terze parti
              </button>
              <a
                href="/cookie"
                className="av-pill av-pill-ghost text-sm"
                data-testid="cookie-manage"
              >
                Gestisci
              </a>
            </div>
          </div>
          <button
            onClick={() => save(false)}
            className="text-[#A39E93] hover:text-[#F7F4EE] shrink-0 self-start"
            aria-label="Chiudi banner (equivale a Rifiuta)"
            data-testid="cookie-close"
          >
            <X size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
